import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { Modal } from "../Modal";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from 'formik';
import instance from '../../utils/axiosInstance';
import { SnackBarMessage } from '../snackBar/snackBar';
import { apiBasePath } from '../../constants/env-url';
import { FEATURE_LIMIT_CONFIG } from '../../utils/config';
import { FEATURE_NAME } from '../../constants/commonConstants';

export interface FeaturePopUpProps {
  userId: number;
  value: number | null;
  onCloseCallBack: () => void;
  featureName: any;
}

const FeaturePopUp = ({ userId, value, onCloseCallBack, featureName }: FeaturePopUpProps) => {
  const [isOpenPopUp, setOpenPopUp] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [msgConfig, setMsgConfig] = useState({ type: '', message: '', snackFlag: false });
  const config = FEATURE_LIMIT_CONFIG[featureName];
  const showUnlimitedToggle = config?.isUnlimit === true;

  const validationSchema = Yup.object().shape({
    limitValue: Yup
      .number()
      .nullable()
      .when("isUnlimited", {
        is: true,
        then: (schema) => showUnlimitedToggle ? schema.notRequired() : schema,
        otherwise: (schema) => schema
          .required(`${config.messageLable} is required`)
          .moreThan(0, `${config.messageLable} should be more than 0`)
      })
  });

  const handleSubmit = (values: { limitValue: number | null, isUnlimited: boolean }) => {
    setLoading(true);
    const payload = buildFeaturePayload(featureName, values, config.featureType);
    instance.post(`${apiBasePath}${config.api}`, payload)
      .then((response) => {
        if (response?.data?.success) {
          handleMessage('success', `${config.messageLable} updated successfully.`);
          setTimeout(() => {
            setOpenPopUp(false);
            onCloseCallBack();
            setMsgConfig((old) => ({ ...old, snackFlag: false }));
          }, 2000);
        } else {
          handleMessage('error', `Unable to update ${config.messageLable}.`);
        }
      })
      .catch(() => {
        handleMessage('error', `Unable to update ${config.messageLable}.`);
      })
      .finally(() => setLoading(false));
  };

  const handleMessage = (type: string, message: string) => {
    setMsgConfig({ type, message, snackFlag: true });
  };

  const buildFeaturePayload = (featureName: string, values: { limitValue: number | null; isUnlimited: boolean }, featureType: string) => {
    const payload: any = { id: userId };
    switch (featureName) {
      case FEATURE_NAME.CHAT:
        payload.maxLimit = values.isUnlimited ? null : values.limitValue;
        payload.featureType = featureType;
        break;
      case FEATURE_NAME.FREE_CHAT_CREDIT:
        payload.freeCredits = values.limitValue ?? null;
        break;
      case FEATURE_NAME.FREE_RENTAL_CREDIT:
        payload.trialCredits = values.limitValue ?? null;
        break;
      case FEATURE_NAME.FREE_BUY_CREDIT:
        payload.trialBuyCredits = values.limitValue ?? null;
        break;
      default:
        handleMessage('error', `Unable to update ${config.messageLable}.`)
    }
    return payload;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
        {featureName === FEATURE_NAME.CHAT ? (
          <Box sx={{ minWidth: '50px', textAlign: 'right' }}>
            {value === null ? 'Unlimited' : value}
          </Box>) : (
          <Box>
            {value}
          </Box>
        )}
        <IconButton onClick={() => setOpenPopUp(true)} sx={{ p: 0 }}>
          <ModeEditIcon style={{ color: "#1976D2", height: '20px', width: '20px' }} />
        </IconButton>
      </Box>
      {isOpenPopUp && (
        <Modal title={`Set ${config.label}`} open={isOpenPopUp} onClose={() => setOpenPopUp(false)}>
          <Box sx={{ py: '5px', px: '10px' }}>
            <Formik
              enableReinitialize
              initialValues={{
                limitValue: value,
                isUnlimited: value === null
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ touched, errors, handleChange, values, setFieldValue, setFieldTouched, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    type="number"
                    fullWidth
                    name="limitValue"
                    label={`${config.label}*`}
                    size="small"
                    value={values.limitValue ?? ''}
                    onChange={handleChange}
                    error={touched.limitValue && Boolean(errors.limitValue)}
                    helperText={<ErrorMessage name="limitValue" />}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ autoComplete: "off" }}
                    margin="dense"
                    disabled={showUnlimitedToggle && values.isUnlimited || msgConfig.snackFlag}
                  />
                  {showUnlimitedToggle && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <Switch
                        checked={values.isUnlimited || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFieldValue("isUnlimited", checked);
                          if (checked) setFieldTouched("limitValue", false);
                        }}
                        color="primary"
                      />
                      <Typography>Unlimited Chat</Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Button onClick={() => setOpenPopUp(false)} type="button" variant="outlined">
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={msgConfig.snackFlag || !dirty}>
                      {isLoading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Update'}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
            {msgConfig.snackFlag && (
              <Box sx={{ position: 'absolute' }}>
                <SnackBarMessage snacBarValue={msgConfig} />
              </Box>
            )}
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default FeaturePopUp;